import { supabase } from "@/lib/supabaseClient";
import { fetchCurrentProfile } from "@/services/auth";

export type ChallengeMetric = "prospects_added" | "openers_sent";

export const METRIC_LABELS: Record<ChallengeMetric, string> = {
  prospects_added: "Prospectos nuevos guardados",
  openers_sent: "Aperturas enviadas",
};

export const METRIC_UNITS: Record<ChallengeMetric, string> = {
  prospects_added: "prospectos",
  openers_sent: "aperturas",
};

export type ChallengeStatus = "upcoming" | "active" | "finished";

export type Challenge = {
  id: string;
  title: string;
  description: string;
  metric: ChallengeMetric;
  target: number;
  startsOn: string; // YYYY-MM-DD
  endsOn: string; // YYYY-MM-DD
  active: boolean;
  imageUrl: string | null;
  participants: number;
  joined: boolean;
};

export type LeaderboardEntry = {
  userId: string;
  fullName: string;
  avatarUrl: string | null;
  progress: number;
};

/** Hoy en formato YYYY-MM-DD según la fecha local del usuario. */
export function todayLocal(): string {
  return new Date().toLocaleDateString("en-CA");
}

export function challengeStatus(c: Pick<Challenge, "startsOn" | "endsOn">): ChallengeStatus {
  const today = todayLocal();
  if (today < c.startsOn) return "upcoming";
  if (today > c.endsOn) return "finished";
  return "active";
}

export async function fetchChallenges(): Promise<Challenge[]> {
  const { data: userData } = await supabase.auth.getUser();
  const me = userData.user?.id ?? null;

  const [challengesRes, participantsRes] = await Promise.all([
    supabase
      .from("challenges")
      .select("id, title, description, metric, target, starts_on, ends_on, active, image_url")
      .order("ends_on", { ascending: false }),
    supabase.from("challenge_participants").select("challenge_id, user_id"),
  ]);
  if (challengesRes.error) throw challengesRes.error;
  if (participantsRes.error) throw participantsRes.error;
  const participants = participantsRes.data ?? [];

  return (challengesRes.data ?? []).map((c) => {
    const mine = participants.filter((p) => p.challenge_id === c.id);
    return {
      id: c.id as string,
      title: c.title as string,
      description: c.description as string,
      metric: c.metric as ChallengeMetric,
      target: c.target as number,
      startsOn: c.starts_on as string,
      endsOn: c.ends_on as string,
      active: c.active as boolean,
      imageUrl: (c.image_url as string | null) ?? null,
      participants: mine.length,
      joined: me ? mine.some((p) => p.user_id === me) : false,
    };
  });
}

export async function fetchLeaderboard(challengeId: string): Promise<LeaderboardEntry[]> {
  const { data, error } = await supabase.rpc("challenge_leaderboard", { p_challenge_id: challengeId });
  if (error) throw error;
  return ((data ?? []) as Array<{ user_id: string; full_name: string | null; avatar_url: string | null; progress: number }>).map(
    (r) => ({
      userId: r.user_id,
      fullName: r.full_name ?? "Miembro",
      avatarUrl: r.avatar_url,
      progress: Number(r.progress ?? 0),
    })
  );
}

export async function joinChallenge(challengeId: string): Promise<void> {
  const profile = await fetchCurrentProfile();
  if (!profile) throw new Error("Debes iniciar sesión.");
  const { error } = await supabase
    .from("challenge_participants")
    .insert({ challenge_id: challengeId, organization_id: profile.organizationId });
  if (error && error.code !== "23505") throw error;
}

export async function leaveChallenge(challengeId: string): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const { error } = await supabase
    .from("challenge_participants")
    .delete()
    .eq("challenge_id", challengeId)
    .eq("user_id", userData.user?.id ?? "");
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Administración (solo admin por RLS)
// ---------------------------------------------------------------------------

export type ChallengeInput = {
  title: string;
  description: string;
  metric: ChallengeMetric;
  target: number;
  startsOn: string;
  endsOn: string;
  imageUrl?: string | null;
};

export async function createChallenge(input: ChallengeInput): Promise<void> {
  const profile = await fetchCurrentProfile();
  if (!profile) throw new Error("Debes iniciar sesión.");
  const { error } = await supabase.from("challenges").insert({
    organization_id: profile.organizationId,
    title: input.title,
    description: input.description,
    metric: input.metric,
    target: input.target,
    starts_on: input.startsOn,
    ends_on: input.endsOn,
    image_url: input.imageUrl || null,
  });
  if (error) throw error;
}

export async function updateChallenge(
  id: string,
  patch: Partial<ChallengeInput & { active: boolean }>
): Promise<void> {
  const row: Record<string, unknown> = {};
  if (patch.title !== undefined) row.title = patch.title;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.metric !== undefined) row.metric = patch.metric;
  if (patch.target !== undefined) row.target = patch.target;
  if (patch.startsOn !== undefined) row.starts_on = patch.startsOn;
  if (patch.endsOn !== undefined) row.ends_on = patch.endsOn;
  if (patch.active !== undefined) row.active = patch.active;
  if (patch.imageUrl !== undefined) row.image_url = patch.imageUrl || null;
  const { error } = await supabase.from("challenges").update(row).eq("id", id);
  if (error) throw error;
}

export async function deleteChallenge(id: string): Promise<void> {
  const { error } = await supabase.from("challenges").delete().eq("id", id);
  if (error) throw error;
}
